import { useState, useCallback, useRef } from 'react';
import { api } from '../../services/api';
import type { Message } from '../../stores/whatsapp/chatStore';

interface AiSuggestionsResult {
  suggestions: string[];
  isLoading: boolean;
  error: string | null;
  fetchSuggestions: () => void;
}

/**
 * Hook que gera sugestoes de resposta via IA usando o endpoint
 * existente POST /api/v1/chat/simples do backend Indicadores.
 *
 * Pega as ultimas mensagens do cliente, monta um prompt contextual
 * e retorna 2-3 sugestoes de resposta profissional.
 */
export function useWaAiSuggestions(
  messages: Message[],
  conversationSubject?: string,
): AiSuggestionsResult {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchRef = useRef<string>('');

  const fetchSuggestions = useCallback(async () => {
    // Pega as ultimas mensagens do cliente
    const clientMessages = messages
      .filter(m => m.sender_type === 'client')
      .slice(-3)
      .map(m => m.content);

    if (clientMessages.length === 0) {
      setSuggestions([]);
      return;
    }

    // Evita chamadas duplicadas para o mesmo contexto
    const contextKey = clientMessages.join('|');
    if (contextKey === lastFetchRef.current && suggestions.length > 0) return;
    lastFetchRef.current = contextKey;

    setIsLoading(true);
    setError(null);

    const subjectCtx = conversationSubject
      ? `O assunto do chamado e: "${conversationSubject}".`
      : '';

    const prompt = `Voce e um assistente de atendimento de um escritorio de contabilidade.
${subjectCtx}
O cliente enviou as seguintes mensagens:
${clientMessages.map((m, i) => `${i + 1}. "${m}"`).join('\n')}

Sugira exatamente 2 respostas curtas e profissionais que o atendente poderia enviar.
Responda APENAS com as 2 sugestoes, uma por linha, sem numeracao, sem aspas, sem explicacao.`;

    try {
      const res = await api.post<{ resposta: string }>(
        '/api/v1/chat/simples',
        { mensagem: prompt },
        { timeout: 30000 },
      );

      const lines = res.data.resposta
        .split('\n')
        .map(l =>
          l
            .replace(/^\d+[.)]\s*/, '')
            .replace(/^["']|["']$/g, '')
            .trim(),
        )
        .filter(l => l.length > 10 && l.length < 300)
        .slice(0, 3);

      setSuggestions(lines);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Erro ao buscar sugestoes da IA');
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, conversationSubject, suggestions.length]);

  return { suggestions, isLoading, error, fetchSuggestions };
}
